import { useEffect, useMemo, useRef, useState } from 'react';
import { useCreateReceipt, useReceiptObligations } from '@/hooks/useReceipts';
import { usePaymentFeeConfig } from '@/hooks/usePaymentFeeConfig';
import { useTenantTimezone } from '@/hooks/useTenantTimezone';
import { computeTenantSlug } from '@/lib/api';
import { tenantCalendarDate } from '@/lib/tenant-dates';
import { useAuthStore } from '@/stores/auth';
import { isCardPaymentMethod, type FeeRuleSet } from '@valiosascrm/shared';
import { IN_PERSON_PAYMENT_METHODS, PAYMENT_METHOD_LABELS } from '@/services/cash-flow';
import type { PaymentMethod } from '@/services/cash-flow';
import type { ReceiptView } from '@/services/receipts';
import {
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  NativeSelect,
  QueryErrorState,
  Skeleton,
  Textarea,
  toast,
} from '@/v2/design-system/primitives';
import { useMoneyFormat } from '../useMoneyFormat';
import { CREDIT_CARD_METHOD, CreditInstallmentsField } from '../CreditInstallmentsField';
import {
  addCreditPart,
  addPart,
  brandChoiceOf,
  creditInstallmentsWithRule,
  buildRequest,
  creditSuggestion,
  errorNotice,
  newComposer,
  partView,
  removePart,
  setAllocation,
  setPartAmount,
  settleBalance,
  textOf,
  totalsOf,
  updatePart,
  fillRemaining,
  type ComposerField,
  type ComposerObligation,
  type ComposerPart,
  type ComposerProblem,
  type ComposerState,
  type ErrorNotice,
} from './receipt-composer-logic';

// Story 65.19 — "Receber em partes": várias formas num só recebimento, com a distribuição de
// cada forma entre as cobranças sempre à vista e uma única confirmação. A linha "Falta receber"
// é o que guia quem está no balcão; o resto é formulário simples, do mesmo jeito do
// "Confirmar recebimento". Falha de validação, conflito ou rede mantém tudo preenchido, e
// confirmar de novo repete o mesmo pedido.

// Composição que saiu da tela sem ser confirmada nem descartada (por exemplo, sessão expirada):
// volta ao reabrir as mesmas cobranças. Vive só na memória desta aba e pertence a quem a
// montou, naquela clínica: outra pessoa ou outra clínica começa do zero e apaga a anterior.
const drafts = new Map<string, ComposerState>();
const uid = () => crypto.randomUUID();

export interface ReceiptComposerProps {
  open: boolean;
  transactionIds: string[];
  onClose: () => void;
  onDone?: (view: ReceiptView) => void;
}

export function ReceiptComposer({ open, transactionIds, onClose, onDone }: ReceiptComposerProps) {
  const owner = useAuthStore((auth) => (auth.user ? `${auth.user.id}:${computeTenantSlug(auth) ?? ''}` : 'sem-sessao'));
  const key = useMemo(() => `${owner}|${[...transactionIds].sort().join(',')}`, [owner, transactionIds]);
  const timezone = useTenantTimezone();
  const today = tenantCalendarDate(timezone);
  const { fmt } = useMoneyFormat();
  const query = useReceiptObligations(transactionIds, open);
  // Story 65.20: máquinas e regras detalhadas da clínica. Sem nenhuma, o diálogo é o de sempre.
  const feeConfig = usePaymentFeeConfig(open);
  const detailedRules: FeeRuleSet | undefined = useMemo(
    () => (feeConfig.data ? { v2: feeConfig.data.rules, legacy: feeConfig.data.legacyRules } : undefined),
    [feeConfig.data],
  );
  const create = useCreateReceipt();
  const [state, setState] = useState<ComposerState | null>(null);
  const [problems, setProblems] = useState<ComposerProblem[]>([]);
  const [notice, setNotice] = useState<ErrorNotice | null>(null);
  const focusAfterRender = useRef<string | null>(null);
  const settled = useRef(false);
  // Um pedido por vez: o segundo clique chega antes de o botão desabilitar, e o React Query só
  // avisa o último `mutate` — o primeiro gravaria e a janela ficaria aberta com o erro do segundo (RC-107).
  const sending = useRef(false);

  const obligations: ComposerObligation[] = useMemo(
    () => (query.data?.obligations ?? [])
      .filter((item) => item.balance > 0 && (item.status === 'pending' || item.status === 'overdue'))
      .map((item) => ({ transactionId: item.transactionId, label: item.serviceName ?? item.description, balance: item.balance, revision: item.revision })),
    [query.data],
  );
  const credit = query.data?.patient?.creditBalance ?? 0;
  const hasPatient = Boolean(query.data?.patient);

  // Abre com o que estava em andamento para as mesmas cobranças, ou com uma forma no valor do saldo.
  useEffect(() => {
    for (const saved of drafts.keys()) if (!saved.startsWith(`${owner}|`)) drafts.delete(saved);
  }, [owner]);
  useEffect(() => {
    if (!open) return;
    settled.current = false;
    setProblems([]);
    setNotice(null);
    setState(drafts.get(key) ?? null);
  }, [open, key]);
  useEffect(() => {
    if (open && obligations.length) setState((current) => current ?? drafts.get(key) ?? newComposer(obligations, uid(), uid));
  }, [open, key, obligations]);
  // Saiu da tela sem confirmar nem descartar: guarda para a volta.
  useEffect(() => () => {
    if (state && !settled.current) drafts.set(key, state);
  }, [state, key]);
  useEffect(() => {
    if (!focusAfterRender.current) return;
    document.getElementById(focusAfterRender.current)?.focus();
    focusAfterRender.current = null;
  });

  const totals = state ? totalsOf(state, obligations) : null;
  const suggestion = state ? creditSuggestion(state, obligations, credit) : 0;
  const change = (next: ComposerState) => {
    setState(next);
    setProblems([]);
  };
  const problemOf = (partId: string | undefined, field: ComposerField) =>
    problems.find((item) => item.field === field && item.partId === partId)?.message;

  const discard = () => {
    settled.current = true;
    drafts.delete(key);
    setState(null);
    onClose();
  };

  const confirm = () => {
    if (!state || sending.current) return;
    const built = buildRequest(state, obligations, today, detailedRules);
    if (!built.request) {
      setProblems(built.problems);
      const first = built.problems[0];
      focusAfterRender.current = first?.partId ? `rc-${first.field}-${first.partId}` : `rc-${first?.field ?? 'parts'}`;
      return;
    }
    setNotice(null);
    sending.current = true;
    create.mutate(built.request, {
      onSuccess: (view) => {
        sending.current = false;
        settled.current = true;
        drafts.delete(key);
        toast.success('Recebimento confirmado!');
        setState(null);
        onDone?.(view);
        onClose();
      },
      onError: (error) => {
        sending.current = false;
        const next = errorNotice(error);
        setNotice(next);
        // O pedido só troca de identidade quando o servidor diz que a anterior não serve mais.
        if (next.newRequest) setState((current) => (current ? { ...current, requestId: uid() } : current));
        if (next.refresh) {
          void query.refetch();
          void feeConfig.refetch();
        }
      },
    });
  };

  const profilesOf = (method: PaymentMethod) => (feeConfig.data?.profiles ?? []).filter((profile) => profile.isActive && profile.method === method);

  const renderPart = (part: ComposerPart, index: number, current: ComposerState) => {
    const view = partView(part);
    const isCredit = part.source === 'patient_credit';
    const profiles = isCredit ? [] : profilesOf(part.method);
    const brandChoice = brandChoiceOf(part, detailedRules);
    const dated = !isCredit && part.date !== '' && part.date !== today;
    return (
      <fieldset key={part.id} data-testid={`receipt-part-${index + 1}`} className="flex flex-col gap-3 rounded-so-md border border-sober-outline p-3">
        <legend className="px-1 text-so-14 font-bold text-sober-text">
          {isCredit ? 'Em Haver da cliente' : `Forma ${index + 1}`}
        </legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {isCredit ? null : (
            <FormField label="Forma de pagamento" htmlFor={`rc-method-${part.id}`}>
              <NativeSelect
                id={`rc-method-${part.id}`}
                value={part.method}
                onChange={(event) => change(updatePart(current, part.id, { method: event.target.value as PaymentMethod, installments: 1, cardBrand: '', profileId: '', excessKind: '' }))}
              >
                {IN_PERSON_PAYMENT_METHODS.map((method) => (
                  <option key={method} value={method}>{PAYMENT_METHOD_LABELS[method]}</option>
                ))}
              </NativeSelect>
            </FormField>
          )}
          {profiles.length ? (
            <FormField label="Máquina ou provedor" htmlFor={`rc-profile-${part.id}`}>
              <NativeSelect
                id={`rc-profile-${part.id}`}
                value={part.profileId}
                onChange={(event) => change(updatePart(current, part.id, { profileId: event.target.value, cardBrand: '' }))}
              >
                <option value="">Não informar</option>
                {profiles.map((profile) => <option key={profile.id} value={profile.id}>{profile.name}</option>)}
              </NativeSelect>
            </FormField>
          ) : null}
          <FormField label={isCredit ? 'Valor do Em Haver a usar (R$)' : 'Valor (R$)'} required htmlFor={`rc-amount-${part.id}`} error={problemOf(part.id, 'amount')}>
            <Input
              id={`rc-amount-${part.id}`}
              inputMode="decimal"
              placeholder="0,00"
              value={part.amount}
              onChange={(event) => change(setPartAmount(current, obligations, part.id, event.target.value))}
            />
          </FormField>
          {isCredit ? null : (
            <FormField
              label={part.method === 'cash' ? 'Entregue pela cliente (R$)' : 'Valor recebido, se diferente (R$)'}
              htmlFor={`rc-received-${part.id}`}
              error={problemOf(part.id, 'received')}
              helper={view.change > 0 ? `Troco: ${fmt(view.change)}. Troco não entra no recebido.` : undefined}
            >
              <Input
                id={`rc-received-${part.id}`}
                inputMode="decimal"
                placeholder={part.amount || '0,00'}
                value={part.received}
                onChange={(event) => change(updatePart(current, part.id, { received: event.target.value }))}
              />
            </FormField>
          )}
          {/* 65.57 (decisão 1A): no crédito, parcelas sempre; sem regra, a taxa fica a conferir. */}
          {part.method === CREDIT_CARD_METHOD && !isCredit ? (
            <CreditInstallmentsField
              id={`rc-installments-${part.id}`}
              value={part.installments}
              onChange={(count) => change(updatePart(current, part.id, { installments: count }))}
              ruleInstallments={creditInstallmentsWithRule(part, detailedRules)}
              missingRuleText={(count) => `Sem taxa cadastrada para ${count}x: a taxa fica a conferir.`}
            />
          ) : null}
          {isCardPaymentMethod(part.method) && !isCredit && brandChoice.decisive ? (
            <FormField label="Bandeira" required htmlFor={`rc-cardBrand-${part.id}`} error={problemOf(part.id, 'cardBrand')} helper="A bandeira muda a taxa deste pagamento.">
              <NativeSelect
                id={`rc-cardBrand-${part.id}`}
                value={part.cardBrand}
                onChange={(event) => change(updatePart(current, part.id, { cardBrand: event.target.value }))}
              >
                <option value="">Escolha</option>
                {brandChoice.brands.map((brand) => <option key={brand.key} value={brand.label}>{brand.label}</option>)}
                <option value="Outra">Outra bandeira</option>
              </NativeSelect>
            </FormField>
          ) : null}
          {isCardPaymentMethod(part.method) && !isCredit && !brandChoice.decisive ? (
            <FormField label="Bandeira (opcional)" htmlFor={`rc-brand-${part.id}`}>
              <Input id={`rc-brand-${part.id}`} value={part.cardBrand} onChange={(event) => change(updatePart(current, part.id, { cardBrand: event.target.value }))} />
            </FormField>
          ) : null}
          {isCredit ? null : (
            <FormField label="Data do pagamento" htmlFor={`rc-date-${part.id}`} error={problemOf(part.id, 'date')}>
              <Input
                id={`rc-date-${part.id}`}
                type="date"
                max={today}
                value={part.date || today}
                onChange={(event) => change(updatePart(current, part.id, { date: event.target.value }))}
              />
            </FormField>
          )}
          {dated ? (
            <FormField label="Motivo da data diferente de hoje" required htmlFor={`rc-dateReason-${part.id}`} error={problemOf(part.id, 'dateReason')}>
              <Input id={`rc-dateReason-${part.id}`} value={part.dateReason} onChange={(event) => change(updatePart(current, part.id, { dateReason: event.target.value }))} />
            </FormField>
          ) : null}
        </div>

        {view.excess > 0 ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label={`O que fazer com ${fmt(view.excess)} que passou do valor?`} required htmlFor={`rc-excess-${part.id}`} error={problemOf(part.id, 'excess')}>
              <NativeSelect
                id={`rc-excess-${part.id}`}
                value={part.excessKind}
                onChange={(event) => change(updatePart(current, part.id, { excessKind: event.target.value as ComposerPart['excessKind'] }))}
              >
                <option value="">Escolha</option>
                <option value="patient_credit" disabled={!hasPatient}>
                  {hasPatient ? 'Guardar como Em Haver da cliente' : 'Guardar como Em Haver (ligue a cobrança a uma cliente)'}
                </option>
                <option value="refund_tracking">Devolver depois (acompanhar a devolução)</option>
              </NativeSelect>
            </FormField>
            {part.excessKind === 'refund_tracking' ? (
              <FormField label="Motivo da devolução (opcional)" htmlFor={`rc-excessReason-${part.id}`}>
                <Input id={`rc-excessReason-${part.id}`} value={part.excessReason} onChange={(event) => change(updatePart(current, part.id, { excessReason: event.target.value }))} />
              </FormField>
            ) : null}
          </div>
        ) : null}

        {obligations.length > 1 ? (
          <div className="flex flex-col gap-2" role="group" aria-label={`Distribuição da ${isCredit ? 'parte de Em Haver' : `forma ${index + 1}`} entre as cobranças`}>
            <p className="text-so-12 font-bold text-sober-secondary">Vai para</p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {obligations.map((item, position) => (
                <FormField key={item.transactionId} label={`${item.label} (R$)`} htmlFor={`rc-allocation-${part.id}-${position}`}>
                  <Input
                    id={`rc-allocation-${part.id}-${position}`}
                    inputMode="decimal"
                    placeholder="0,00"
                    value={part.allocations[item.transactionId] ?? ''}
                    onChange={(event) => change(setAllocation(current, part.id, item.transactionId, event.target.value))}
                  />
                </FormField>
              ))}
            </div>
            <div>
              <Button type="button" variant="secondary" size="sm" onClick={() => change(fillRemaining(current, obligations, part.id))}>
                Usar o restante
              </Button>
            </div>
          </div>
        ) : null}
        {problemOf(part.id, 'allocations') || problemOf(part.id, 'parts') ? (
          <p id={`rc-allocations-${part.id}`} tabIndex={-1} role="alert" className="text-so-12 text-sober-red">
            {problemOf(part.id, 'allocations') ?? problemOf(part.id, 'parts')}
          </p>
        ) : null}

        {current.parts.length > 1 ? (
          <div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                focusAfterRender.current = 'rc-add-part';
                change(removePart(current, part.id));
              }}
            >
              {isCredit ? 'Remover Em Haver' : `Remover forma ${index + 1}`}
            </Button>
          </div>
        ) : null}
      </fieldset>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && discard()}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Receber em partes</DialogTitle>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4">
          {query.isLoading ? <Skeleton className="h-24 w-full" /> : null}
          {query.isError && !query.data ? (
            <QueryErrorState title="Não foi possível carregar as cobranças" onRetry={() => void query.refetch()} />
          ) : null}
          {query.data && !obligations.length ? (
            <p className="text-so-14 text-sober-secondary">Nenhuma cobrança em aberto aqui. Feche e confira a lista.</p>
          ) : null}

          {state && totals && obligations.length ? (
            <>
              <section aria-label="Cobranças deste recebimento" className="flex flex-col gap-1">
                {obligations.map((item) => {
                  const mine = totals.perObligation.find((row) => row.transactionId === item.transactionId);
                  const left = Math.max(0, mine?.remaining ?? item.balance);
                  return (
                    <div key={item.transactionId} className="flex flex-wrap items-center justify-between gap-2 text-so-14">
                      <span className="min-w-0 break-words text-sober-text">{item.label}</span>
                      <span className="flex items-center gap-2 tabular-nums text-sober-secondary">
                        {fmt(item.balance)}
                        {left === 0 ? <Badge variant="green">quitada</Badge> : <Badge variant="yellow">falta {fmt(left)}</Badge>}
                      </span>
                    </div>
                  );
                })}
              </section>

              <p data-testid="receipt-remaining" aria-live="polite" className="rounded-so-md bg-sober-muted px-3 py-2 text-so-14 text-sober-text">
                {totals.remaining > 0 ? (
                  <>Falta receber <strong className="tabular-nums">{fmt(totals.remaining)}</strong></>
                ) : (
                  <strong>Tudo recebido</strong>
                )}
              </p>

              {state.parts.map((part, index) => renderPart(part, index, state))}
              {problemOf(undefined, 'parts') ? (
                <p id="rc-parts" tabIndex={-1} role="alert" className="text-so-12 text-sober-red">{problemOf(undefined, 'parts')}</p>
              ) : null}

              <div className="flex flex-wrap gap-2">
                <Button
                  id="rc-add-part"
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    const next = addPart(state, obligations, uid);
                    focusAfterRender.current = `rc-method-${next.parts[next.parts.length - 1].id}`;
                    change(next);
                  }}
                >
                  Adicionar forma
                </Button>
                {totals.remaining > 0 ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      const next = settleBalance(state, obligations, uid);
                      focusAfterRender.current = `rc-method-${next.parts[next.parts.length - 1].id}`;
                      change(next);
                    }}
                  >
                    Quitar saldo
                  </Button>
                ) : null}
                {suggestion > 0 ? (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      const next = addCreditPart(state, obligations, suggestion, uid);
                      focusAfterRender.current = `rc-amount-${next.parts[next.parts.length - 1].id}`;
                      change(next);
                    }}
                  >
                    Usar Em Haver: {fmt(suggestion)}
                  </Button>
                ) : null}
              </div>
              {credit > 0 ? (
                <p className="text-so-12 text-sober-secondary">Em Haver disponível da cliente: {fmt(credit)}. Só entra se você confirmar o valor.</p>
              ) : null}

              {totals.partial ? (
                <FormField label="Motivo do recebimento parcial" required htmlFor="rc-partialReason" error={problemOf(undefined, 'partialReason')}>
                  <Textarea
                    id="rc-partialReason"
                    rows={2}
                    value={state.partialReason}
                    onChange={(event) => change({ ...state, partialReason: event.target.value })}
                  />
                </FormField>
              ) : null}

              <section aria-label="Resumo do recebimento" data-testid="receipt-summary" className="flex flex-col gap-1 border-t border-sober-outline pt-3 text-so-14 text-sober-text">
                <p>
                  Recebido nas cobranças: <strong className="tabular-nums">{fmt(totals.applied)}</strong> em {state.parts.length} {state.parts.length === 1 ? 'forma' : 'formas'}.
                </p>
                {totals.creditUsed > 0 ? <p>Desse valor, {fmt(totals.creditUsed)} é Em Haver da cliente (não entra como dinheiro).</p> : null}
                {totals.change > 0 ? <p>Troco a devolver agora: {fmt(totals.change)}.</p> : null}
                {totals.excess > 0 ? <p>Recebido a mais: {fmt(totals.excess)}.</p> : null}
                {totals.partial ? <p>Fica em aberto: {fmt(totals.outstanding)}.</p> : null}
              </section>
            </>
          ) : null}

          {notice ? (
            <div role="alert" className="flex flex-col gap-2 rounded-so-md bg-sober-red-bg px-3 py-2 text-so-14 text-sober-red">
              <p>{notice.message}</p>
              {notice.refresh ? (
                <div>
                  <Button type="button" variant="secondary" size="sm" onClick={() => void query.refetch()} disabled={query.isFetching}>
                    {query.isFetching ? 'Atualizando…' : 'Atualizar cobranças'}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={discard}>Voltar</Button>
          <Button type="button" onClick={confirm} disabled={!state || !obligations.length || create.isPending}>
            {create.isPending ? 'Confirmando…' : 'Confirmar recebimento'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Só para teste: esvazia as composições guardadas em memória. */
export function clearReceiptComposerDrafts() {
  drafts.clear();
}

export { textOf as receiptAmountText };
